import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { motion } from 'framer-motion';
import { Download, Users, FileText, RefreshCw, AlertCircle } from 'lucide-react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from '../firebase';
import {
  getAllMidnightSurveys,
  exportSurveysToCSV,
  MidnightSurveyResponse,
  ROLE_CLASSES,
  RoleType,
} from '../services/midnightSurveyService';

const MidnightSurveyAdmin: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [surveys, setSurveys] = useState<MidnightSurveyResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userCharacterNames, setUserCharacterNames] = useState<Map<string, string>>(new Map());
  
  // Admin check
  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    
    loadSurveys();
  }, [authLoading, user, navigate]);

  const loadSurveys = async () => {
    setLoading(true);
    setError(null);
    try {
      const surveyData = await getAllMidnightSurveys();
      setSurveys(surveyData);
      
      // Fetch main character names for all users
      const db = getFirestore(app);
      const characterNamesMap = new Map<string, string>();
      
      // Get unique user IDs from surveys
      const uniqueUserIds = new Set(surveyData.map(s => s.userId));
      
      // Fetch user documents to get mainCharacterName
      await Promise.all(
        Array.from(uniqueUserIds).map(async (userId) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              const mainCharacterName = userDoc.data().mainCharacterName;
              if (mainCharacterName) {
                characterNamesMap.set(userId, mainCharacterName);
              }
            }
          } catch (err) {
            console.warn(`Failed to fetch character name for user ${userId}:`, err);
          }
        })
      );
      
      setUserCharacterNames(characterNamesMap);
    } catch (err: any) {
      setError(err.message || 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (surveys.length === 0) {
      setError('No survey data to export');
      return;
    }

    const csvContent = exportSurveysToCSV(surveys);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `midnight_survey_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get all unique classes for table header
  const allClasses = new Set<string>();
  Object.values(ROLE_CLASSES).forEach(classes => {
    classes.forEach(className => allClasses.add(className));
  });
  const sortedClasses = Array.from(allClasses).sort();

  // Map general thoughts response to shorter version for table display
  const mapGeneralThoughts = (thoughts: string): string => {
    const mapping: { [key: string]: string } = {
      'I know what I want to play': 'Locked in',
      'I have some ideas on what I want to play': 'Debating some options',
      'I am open to filling raid team needs': 'Filler',
      "I don't know what I want to play": 'No idea',
    };
    return mapping[thoughts] || thoughts;
  };

  // Format role name for display (shorten Melee DPS and Ranged DPS)
  const formatRoleName = (roleType: RoleType): string => {
    if (roleType === 'Melee DPS') return 'Melee';
    if (roleType === 'Ranged DPS') return 'Ranged';
    return roleType;
  };

  // Get all selected roles for a survey (all roles not marked as absolutely not)
  const getAllSelectedRoles = (survey: MidnightSurveyResponse): RoleType[] => {
    const selectedRoles: RoleType[] = [];
    
    for (const roleType of Object.keys(survey.roles) as RoleType[]) {
      const role = survey.roles[roleType];
      if (!role.absolutelyNot) {
        selectedRoles.push(roleType);
      }
    }
    
    return selectedRoles;
  };

  // Get primary role for a survey (highest interest level) - kept for rating display
  const getPrimaryRole = (survey: MidnightSurveyResponse): { role: RoleType | null; rating: number } => {
    let primaryRole: RoleType | null = null;
    let maxInterest = 0;
    
    for (const roleType of Object.keys(survey.roles) as RoleType[]) {
      const role = survey.roles[roleType];
      if (!role.absolutelyNot && role.interestLevel > maxInterest) {
        maxInterest = role.interestLevel;
        primaryRole = roleType;
      }
    }
    
    return { role: primaryRole, rating: maxInterest };
  };

  // Check if a class is selected in any role for a survey
  const isClassSelected = (survey: MidnightSurveyResponse, className: string): boolean => {
    for (const roleType of Object.keys(survey.roles) as RoleType[]) {
      const role = survey.roles[roleType];
      if (role.classes.includes(className)) {
        return true;
      }
    }
    return false;
  };

  // Calculate role totals (count of people who selected each role - not marked as absolutely not)
  const calculateRoleTotals = () => {
    const totals: { [key in RoleType]: number } = {
      'Tank': 0,
      'Healer': 0,
      'Melee DPS': 0,
      'Ranged DPS': 0,
    };

    surveys.forEach(survey => {
      (Object.keys(survey.roles) as RoleType[]).forEach(roleType => {
        const role = survey.roles[roleType];
        if (!role.absolutelyNot) {
          totals[roleType]++;
        }
      });
    });

    return totals;
  };

  // Calculate class totals per role (how many people selected each class for each role)
  const calculateClassTotalsByRole = () => {
    const totals: { [roleType: string]: { [className: string]: number } } = {};

    (Object.keys(ROLE_CLASSES) as RoleType[]).forEach(roleType => {
      totals[roleType] = {};
      ROLE_CLASSES[roleType].forEach(className => {
        totals[roleType][className] = 0;
      });
    });

    surveys.forEach(survey => {
      (Object.keys(survey.roles) as RoleType[]).forEach(roleType => {
        const role = survey.roles[roleType];
        if (!role.absolutelyNot) {
          role.classes.forEach(className => {
            if (totals[roleType][className] !== undefined) {
              totals[roleType][className]++;
            }
          });
        }
      });
    });

    return totals;
  };

  // Calculate total class selections (across all roles) for the table footer
  // Count each class once per player, not per row
  const calculateClassTotals = () => {
    const totals: { [className: string]: number } = {};
    
    sortedClasses.forEach(className => {
      totals[className] = 0;
    });

    surveys.forEach(survey => {
      sortedClasses.forEach(className => {
        if (isClassSelected(survey, className)) {
          totals[className]++;
        }
      });
    });

    return totals;
  };

  // Expand surveys into rows - one row per player per role
  interface ExpandedRow {
    surveyId: string;
    userId: string;
    username: string;
    mainCharacterName: string | null;
    generalThoughts: string;
    roleType: RoleType;
    roleRating: number;
    classes: string[];
  }

  const expandSurveysIntoRows = (): ExpandedRow[] => {
    const rows: ExpandedRow[] = [];
    
    surveys.forEach(survey => {
      const selectedRoles = getAllSelectedRoles(survey);
      
      selectedRoles.forEach(roleType => {
        const role = survey.roles[roleType];
        const mainCharacterName = userCharacterNames.get(survey.userId) || null;
        rows.push({
          surveyId: survey.id,
          userId: survey.userId,
          username: survey.username,
          mainCharacterName,
          generalThoughts: mapGeneralThoughts(survey.generalThoughts),
          roleType,
          roleRating: role.interestLevel,
          classes: role.classes,
        });
      });
    });
    
    return rows;
  };

  const roleTotals = calculateRoleTotals();
  const classTotalsByRole = calculateClassTotalsByRole();
  const classTotals = calculateClassTotals();
  const expandedRows = expandSurveysIntoRows();

  if (authLoading || loading) {
    return (
      <div className="page-container bg-surface flex items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  return (
    <div className="page-container bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">
              Midnight Season 2 Survey Results
            </span>
          </h1>
        </motion.div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-3"
          >
            <AlertCircle className="text-red-400" size={20} />
            <p className="text-red-400">{error}</p>
          </motion.div>
        )}

        {/* Stats and Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="glass p-6 rounded-xl mb-8"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-primary/20 p-4 rounded-lg">
                <Users className="text-primary" size={32} />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-1">
                  <span className="gradient-text">Total Responses</span>
                </h3>
                <p className="text-3xl font-bold text-text-primary">{surveys.length}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadSurveys}
                className="px-6 py-3 bg-surface-light border border-border text-text-primary rounded-lg font-semibold hover:bg-surface transition-colors flex items-center gap-2"
              >
                <RefreshCw size={20} />
                Refresh
              </button>
              <button
                onClick={handleExportCSV}
                disabled={surveys.length === 0}
                className="px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2"
              >
                <Download size={20} />
                Export CSV
              </button>
            </div>
          </div>
        </motion.div>

        {surveys.length > 0 ? (
          <>
            {/* Role and Class Totals Summary - Separate Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="glass p-6 rounded-xl mb-8"
            >
              <h2 className="text-2xl font-bold text-text-primary mb-6">
                <span className="gradient-text">Role & Class Breakdown</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {(Object.keys(ROLE_CLASSES) as RoleType[]).map((roleType) => (
                  <div key={roleType} className="bg-surface-light rounded-lg p-4 border-2 border-primary/30">
                    <h3 className="text-lg font-bold text-text-primary mb-3">
                      {roleType}
                      <span className="block text-sm text-primary mt-1">Total: {roleTotals[roleType]}</span>
                    </h3>
                    <div className="space-y-2">
                      {ROLE_CLASSES[roleType].map((className) => (
                        <div key={className} className="flex justify-between items-center text-sm py-1 border-b border-border/50">
                          <span className="text-text-secondary">{className}</span>
                          <span className="text-primary font-bold">
                            {classTotalsByRole[roleType]?.[className] || 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Survey Results Table - Separate Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="glass p-6 rounded-xl overflow-x-auto"
            >
              <h2 className="text-2xl font-bold text-text-primary mb-6">
                <span className="gradient-text">Individual Responses</span>
              </h2>
              <div className="min-w-full">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-border">
                    <th className="text-left p-3 font-semibold text-text-primary sticky left-0 bg-surface z-10">
                      User/Player
                    </th>
                    <th className="text-left p-3 font-semibold text-text-primary">Mindset</th>
                    <th className="text-left p-3 font-semibold text-text-primary">Role</th>
                    <th className="text-left p-3 font-semibold text-text-primary">Rating</th>
                      {sortedClasses.map((className) => (
                        <th
                          key={className}
                          className="text-left p-3 font-semibold text-text-primary min-w-[120px]"
                        >
                          {className}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                  {expandedRows.map((row, index) => {
                    // Check if this class is selected for this specific role
                    const isClassSelectedForRole = (className: string): boolean => {
                      return row.classes.includes(className);
                    };
                    
                    return (
                      <tr
                        key={`${row.userId}-${row.roleType}-${index}`}
                        className={`border-b border-border hover:bg-surface-light transition-colors ${
                          index % 2 === 0 ? 'bg-surface/50' : ''
                        }`}
                      >
                        <td className="p-3 text-text-primary font-medium sticky left-0 bg-inherit z-10">
                          {row.mainCharacterName || row.username}
                        </td>
                        <td className="p-3 text-text-secondary">{row.generalThoughts}</td>
                        <td className="p-3 text-text-secondary">{formatRoleName(row.roleType)}</td>
                        <td className="p-3 text-text-secondary text-center">{row.roleRating}</td>
                          {sortedClasses.map((className) => (
                            <td key={className} className="p-3 text-center">
                              {isClassSelectedForRole(className) ? (
                                <span className="text-primary font-bold">X</span>
                              ) : (
                                <span className="text-text-secondary">-</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-primary/50 bg-primary/10">
                      <td className="p-3 text-text-primary font-bold sticky left-0 bg-primary/10 z-10">
                        TOTALS
                      </td>
                      <td className="p-3 text-text-secondary"></td>
                      <td className="p-3 text-text-secondary"></td>
                      <td className="p-3 text-text-secondary"></td>
                      {sortedClasses.map((className) => (
                        <td key={className} className="p-3 text-center">
                          <span className="text-primary font-bold">{classTotals[className] || 0}</span>
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </motion.div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="glass p-12 rounded-xl text-center"
          >
            <FileText className="text-text-secondary mx-auto mb-4" size={64} />
            <h3 className="text-2xl font-semibold text-text-primary mb-2">No Survey Responses Yet</h3>
            <p className="text-text-secondary">
              Survey responses will appear here once users start submitting their preferences.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MidnightSurveyAdmin;

